'use client';

import { createContext, PropsWithChildren, useContext, useState } from 'react';

const ConfirmationDialog = ({
  open,
  title,
  message,
  onConfirm,
  onDismiss,
}: {
  open: boolean;
  title?: string;
  message?: string;
  onConfirm: () => void;
  onDismiss: () => void;
}) => {
  return (
    <dialog className="modal" open={open}>
      <div className="modal-box">
        <h3 className="text-lg font-bold">{title}</h3>
        <p className="py-4">{message}</p>
        <div className="modal-action">
          <form method="dialog" className="space-x-3">
            <button className="btn" onClick={onDismiss}>
              Abort
            </button>
            <button className="btn btn-error" onClick={onConfirm}>
              Continue
            </button>
          </form>
        </div>
      </div>
    </dialog>
  );
};

const ConfirmationDialogContext = createContext<{
  openDialog: (value: {
    title: string;
    message: string;
    actionCallback: (result: boolean) => void;
  }) => void;
}>({
  openDialog: () => {
    throw new Error('ConfirmationDialogContext not initialized');
  },
});

const ConfirmationDialogProvider = ({ children }: PropsWithChildren) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogConfig, setDialogConfig] = useState<
    { title: string; message: string; actionCallback: (result: boolean) => void } | undefined
  >();

  const openDialog = (props: NonNullable<typeof dialogConfig>) => {
    setDialogOpen(true);
    setDialogConfig(props);
  };

  const resetDialog = () => {
    setDialogOpen(false);
    setDialogConfig(undefined);
  };

  const onConfirm = () => {
    resetDialog();
    dialogConfig?.actionCallback(true);
  };

  const onDismiss = () => {
    resetDialog();
    dialogConfig?.actionCallback(false);
  };

  return (
    <ConfirmationDialogContext value={{ openDialog }}>
      <ConfirmationDialog
        open={dialogOpen}
        title={dialogConfig?.title}
        message={dialogConfig?.message}
        onConfirm={onConfirm}
        onDismiss={onDismiss}
      />
      {children}
    </ConfirmationDialogContext>
  );
};

const useConfirmationDialog = () => {
  const { openDialog } = useContext(ConfirmationDialogContext);

  const getConfirmation = (modalOptions: { title: string; message: string }) =>
    new Promise<boolean>((res) => {
      openDialog({ ...modalOptions, actionCallback: res });
    });

  return { getConfirmation };
};

export default ConfirmationDialog;
export { ConfirmationDialogProvider, useConfirmationDialog };
